// M9.3 — /share/:token. Public, read-only report shell. No app chrome,
// no sidebar, no Connect/Settings/Exports, no property picker, no
// sign-out, no Download buttons. Meta robots=noindex is set here and on
// the JSON endpoint's X-Robots-Tag so scrapers and link previews can't
// cache the snapshot.

import { useEffect, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { SkedMark } from "../components/SkedMark";
import { Button } from "../components/ui/button";
import { ShareButtonsRow } from "../components/share/ShareButtonsRow";
import { TooltipProvider } from "../components/ui/tooltip";
import { DateRangeChip } from "../components/report/DateRangeChip";
import { FrozenReportSections } from "../components/report/FrozenReportSections";
import { buildSkedHref } from "../../shared/leadgen/cta-rules";
import {
  fetchSharedReport,
  type InsightsResult,
  type ReportCompare,
  type ReportPayload,
  type ReportRange,
  type SharedReportProperty,
  type InsightsV2,
} from "../lib/api";
import { usePageMeta } from "../hooks/usePageMeta";
import { pushGtmEvent } from "../lib/gtm-events";
import { reportTelemetry } from "../lib/telemetry";

type Status =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "not_found" }
  | { kind: "expired"; expiresAt: number }
  | {
      kind: "ready";
      report: ReportPayload;
      property: SharedReportProperty;
      createdAt: number;
      // M13.6 — frozen AI insights baked into the v3 envelope at mint
      // time. Null on legacy v1/v2 envelopes (the viewer simply omits
      // the section). No regenerate button on the public surface.
      insights: InsightsResult | null;
      // SKE-3458 — V2 structured insights on v4 envelopes. When present,
      // FrozenInsightsSection renders the headline + preview + "See more
      // detail" disclosure + action cards (same layout as the owner's
      // dashboard). Null / undefined on older envelopes — falls back to
      // the legacy `insights` render path.
      insightsV2: InsightsV2 | null;
    };

export function SharedReportPage() {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<Status>({ kind: "loading" });

  useEffect(() => {
    // noindex for crawlers that don't honour the server-side X-Robots-Tag
    // (eg. link-preview bots that hit the SPA shell first, before the JSON
    // API). Also cleared on unmount so the same tab navigating onwards
    // doesn't leak the meta tag.
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setStatus({ kind: "not_found" });
      return;
    }
    let cancelled = false;
    setStatus({ kind: "loading" });
    (async () => {
      try {
        const result = await fetchSharedReport(token);
        if (cancelled) return;
        if (result.kind === "ok")
          setStatus({
            kind: "ready",
            report: result.report,
            property: result.property,
            createdAt: result.createdAt,
            insights: result.insights ?? null,
            insightsV2: result.insightsV2 ?? null,
          });
        else if (result.kind === "expired")
          setStatus({ kind: "expired", expiresAt: result.expiresAt });
        else setStatus({ kind: "not_found" });
      } catch (e) {
        if (cancelled) return;
        setStatus({ kind: "error", message: (e as Error).message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const property = status.kind === "ready" ? status.property : null;
  usePageMeta({
    title: property?.propertyName
      ? `Shared traffic report — ${property.propertyName} · Traffic Snapshot`
      : "Shared traffic report · Traffic Snapshot",
    description:
      "Read-only Traffic Snapshot report shared by a Sked Social customer. Snapshot data only — no login required.",
  });

  return (
    <TooltipProvider delayDuration={150} skipDelayDuration={300}>
      <div className="min-h-dvh bg-white text-ink antialiased">
        <ShareHeader property={property} />
        <main className="mx-auto max-w-[1160px] px-6 py-10 sm:px-10 sm:py-14">
          {renderBody(status)}
        </main>
        <ShareFooter />
      </div>
    </TooltipProvider>
  );
}

function renderBody(status: Status): ReactNode {
  if (status.kind === "loading") return <LoadingShell />;
  if (status.kind === "not_found") return <NotFoundPanel />;
  if (status.kind === "expired") return <ExpiredPanel expiresAt={status.expiresAt} />;
  if (status.kind === "error") return <ErrorPanel message={status.message} />;
  return (
    <SharedReport
      report={status.report}
      property={status.property}
      createdAt={status.createdAt}
      insights={status.insights}
      insightsV2={status.insightsV2}
    />
  );
}

// Nav linking the shared viewer to the public-friendly tools from the
// authenticated app. UTM Health Check is fully public; Builder and
// Taxonomy require sign-in — the link still works, auth-gating kicks in
// once the visitor clicks through.
const TOOLS_NAV: ReadonlyArray<{ label: string; to: string; icon: string }> = [
  { label: "Tag health", to: "/utm-health-check", icon: "fa-regular fa-shield-check" },
  { label: "UTM builder", to: "/app/builder", icon: "fa-regular fa-wrench" },
  { label: "Taxonomy", to: "/app/taxonomy", icon: "fa-regular fa-list-tree" },
];

const SHARE_VIEWER_HEADER_HREF = buildSkedHref({
  medium: "share_viewer",
  campaign: "try_sked",
  content: "header_button",
});
const SHARE_VIEWER_FOOTER_LINK_HREF = buildSkedHref({
  medium: "share_viewer",
  campaign: "try_sked",
  content: "footer_link",
});
const SHARE_VIEWER_FOOTER_BUTTON_HREF = buildSkedHref({
  medium: "share_viewer",
  campaign: "try_sked",
  content: "footer_button",
});

// M15 — recipient second CTA. Router Link (not buildSkedHref): the
// target is the TOOL's own landing page, and <BrowserRouter basename>
// keeps it under /tools/traffic-snapshot in production. The UTM params
// ride along so a recipient who connects their own GA4 attributes back
// to the share loop.
const RECIPIENT_TOOL_CTA_TO =
  "/?utm_source=traffic-snapshot&utm_medium=share_viewer&utm_campaign=recipient_tool_cta";

function fireRecipientToolCta(): void {
  // Anonymous surface — POST /api/telemetry accepts this event without
  // a session (rate-limited per IP server-side).
  reportTelemetry({ event: "share_recipient_tool_cta_clicked" });
  pushGtmEvent("share_recipient_tool_cta_clicked");
}

function ShareHeader({ property }: { property: SharedReportProperty | null }) {
  return (
    <header className="border-b border-neutral-200 bg-white">
      <div className="mx-auto flex max-w-[1160px] flex-col gap-4 px-6 py-4 sm:px-10 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <SkedMark className="h-7 w-auto" />
          <div className="flex flex-col leading-tight">
            <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-purple-700">
              Traffic Snapshot <span className="text-neutral-500">from Sked Social</span>
            </span>
            <span className="text-xs text-neutral-500">
              Shared report · read-only
              {property && property.propertyName !== "Shared report" ? (
                <>
                  {" · "}
                  <span className="font-medium text-neutral-700">{property.propertyName}</span>
                </>
              ) : null}
            </span>
          </div>
        </div>
        <nav className="flex flex-wrap items-center gap-1.5" aria-label="Related Sked Social tools">
          {TOOLS_NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-neutral-700 ring-1 ring-neutral-950/10 transition-colors hover:bg-neutral-50 focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
            >
              <i className={`${item.icon} text-[11px] text-purple-600`} aria-hidden="true" />
              {item.label}
            </Link>
          ))}
          <a
            href={SHARE_VIEWER_HEADER_HREF}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full bg-purple-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm ring-1 ring-purple-600 transition-colors hover:bg-purple-700 focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
          >
            Try Sked Social
            <i
              className="fa-regular fa-arrow-up-right-from-square text-[10px]"
              aria-hidden="true"
            />
          </a>
        </nav>
      </div>
    </header>
  );
}

function ShareFooter() {
  return (
    <footer className="mt-16 border-t border-neutral-200 bg-neutral-50">
      <div className="mx-auto flex max-w-[1160px] flex-col items-start gap-3 px-6 py-8 sm:flex-row sm:items-center sm:justify-between sm:px-10">
        <p className="text-sm text-neutral-600">
          Made with{" "}
          <Link to="/" className="font-semibold text-purple-700 underline-offset-2 hover:underline">
            Sked Social Traffic Snapshot
          </Link>
          {" — "}
          <a
            href={SHARE_VIEWER_FOOTER_LINK_HREF}
            target="_blank"
            rel="noreferrer"
            className="underline-offset-2 hover:underline"
          >
            from Sked Social
          </a>
          .
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {/* M15 — visually-secondary recipient CTA next to the primary
              Try Sked button. Same rounded-pill grammar as the header
              tools nav so it reads as a supporting action. */}
          <Link
            to={RECIPIENT_TOOL_CTA_TO}
            onClick={fireRecipientToolCta}
            className="inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-2 text-sm font-semibold text-purple-700 ring-1 ring-neutral-950/10 transition-colors hover:bg-purple-50 focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
          >
            Get this report for your own site — free
          </Link>
          <Button asChild>
            <a href={SHARE_VIEWER_FOOTER_BUTTON_HREF} target="_blank" rel="noreferrer">
              Try Sked Social →
            </a>
          </Button>
        </div>
      </div>
    </footer>
  );
}

function LoadingShell() {
  return (
    <div className="space-y-10" aria-busy="true">
      <div className="h-10 w-64 animate-pulse rounded-md bg-neutral-100" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-28 animate-pulse rounded-xl bg-neutral-100" />
        ))}
      </div>
      <div className="h-[240px] animate-pulse rounded-2xl bg-neutral-100" />
    </div>
  );
}

function NotFoundPanel() {
  return (
    <section className="mx-auto max-w-lg rounded-2xl bg-white p-10 text-center ring-1 ring-neutral-950/5">
      <i className="fa-regular fa-link-slash text-3xl text-neutral-400" aria-hidden="true" />
      <h1 className="mt-4 font-display text-2xl font-semibold text-neutral-950">
        This share link doesn't exist
      </h1>
      <p className="mt-2 text-sm text-neutral-600">
        It may have been revoked or mistyped. Check with whoever sent you the link.
      </p>
      <div className="mt-6">
        <Button asChild>
          <Link to="/">Go to Sked Social Traffic Snapshot</Link>
        </Button>
      </div>
    </section>
  );
}

function ExpiredPanel({ expiresAt }: { expiresAt: number }) {
  return (
    <section className="mx-auto max-w-lg rounded-2xl bg-white p-10 text-center ring-1 ring-neutral-950/5">
      <i className="fa-regular fa-clock text-3xl text-amber-500" aria-hidden="true" />
      <h1 className="mt-4 font-display text-2xl font-semibold text-neutral-950">
        This share link has expired
      </h1>
      <p className="mt-2 text-sm text-neutral-600">
        The owner set a lifetime that ended on{" "}
        <strong className="font-semibold text-neutral-800">
          {new Date(expiresAt).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}
        </strong>
        . Ask them to mint a fresh link if you still need this report.
      </p>
      <div className="mt-6">
        <Button asChild>
          <Link to="/?utm_source=traffic-snapshot&utm_medium=share-expired&utm_campaign=share-viewer">
            See how Traffic Snapshot works
          </Link>
        </Button>
      </div>
    </section>
  );
}

function ErrorPanel({ message }: { message: string }) {
  return (
    <section className="mx-auto max-w-lg rounded-2xl bg-white p-10 text-center ring-1 ring-neutral-950/5">
      <i className="fa-regular fa-triangle-exclamation text-3xl text-red-500" aria-hidden="true" />
      <h1 className="mt-4 font-display text-2xl font-semibold text-neutral-950">
        Something went wrong
      </h1>
      <p className="mt-2 text-sm text-neutral-600">{message}</p>
    </section>
  );
}

function SharedReport({
  report,
  property,
  createdAt,
  insights,
  insightsV2,
}: {
  report: ReportPayload;
  property: SharedReportProperty;
  createdAt: number;
  insights: InsightsResult | null;
  insightsV2: InsightsV2 | null;
}) {
  const { meta } = report;
  const hasPropertyName = property.propertyName && property.propertyName !== "Shared report";
  const snapshotDate = new Date(createdAt).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return (
    <div className="space-y-10">
      <header>
        <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-purple-700">
          Traffic report
        </p>
        <span aria-hidden="true" className="mt-1.5 block h-[3px] w-8 rounded-full bg-purple-500" />
        <h1 className="mt-3 font-display text-3xl font-semibold text-neutral-950 sm:text-4xl">
          Social traffic <em className="font-serif text-purple-700">at a glance</em>
          {hasPropertyName ? (
            <span className="block text-lg font-semibold text-neutral-600 sm:text-xl">
              for <span className="text-neutral-900">{property.propertyName}</span>
            </span>
          ) : null}
        </h1>
        {/* M12.3 — shared report locks to the range + compare it was minted
            with. DateRangeChip shows the resolved ISO window (plus compare
            clause) so the viewer sees the same label the owner saw when they
            created the link, regardless of preset vs. custom. */}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <DateRangeChip
            startDate={meta.periodStart}
            endDate={meta.periodEnd}
            compare={meta.compare}
            comparePeriodStart={meta.comparePeriodStart}
            comparePeriodEnd={meta.comparePeriodEnd}
          />
          <span className="text-xs text-neutral-500">Snapshot taken {snapshotDate}</span>
        </div>
        {/* M15 — pass-it-on row. The viewer's own URL IS the share URL
            (token included), so recipients can forward the report
            without asking the owner for a fresh link. */}
        <div className="mt-4">
          <ShareButtonsRow
            shareUrl={window.location.href}
            surface="share_viewer"
            propertyName={hasPropertyName ? property.propertyName : "our site"}
          />
        </div>
      </header>

      <FrozenReportSections
        report={report}
        insights={insights}
        insightsV2={insightsV2}
        propertyLabel="shared report"
      />
    </div>
  );
}

// Re-export only for type narrowing consumers; the public export stays the page.
export type { ReportCompare, ReportRange };
